import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { usePlaces } from '@/hooks/usePlaces';
import { useFavorites } from '@/hooks/useFavorites';
import { HOME } from '@shared/home';
import { CategoryChipRow } from '@/components/CategoryChipRow';
import { LocalityFilterRow } from '@/components/LocalityFilterRow';
import { PlaceCard } from '@/components/PlaceCard';
import { parsePlaceFilterCategory, type PlaceFilterCategory } from '@/lib/interests';
import { parseLocalityFilter, placeMatchesLocality, type LocalityFilter } from '@shared/locality';
import type { PlaceWithDistance } from '@shared/types';
import { appendEnglish } from '@shared/ptEn';

const navyIcon = L.divIcon({
  className: 'map-pin',
  html: '<span class="map-pin__dot"></span>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

const goldIcon = L.divIcon({
  className: 'map-pin map-pin--home',
  html: '<span class="map-pin__dot map-pin__dot--home"></span>',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

export function MapPage() {
  const { places } = usePlaces();
  const { isFavorite, toggle } = useFavorites();
  const [params, setParams] = useSearchParams();
  const locality = parseLocalityFilter(params.get('locality'));
  const category = parsePlaceFilterCategory(params.get('category'));
  const mapRef = useRef<HTMLDivElement>(null);
  const mapObj = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);
  const [selected, setSelected] = useState<PlaceWithDistance | null>(null);

  const visible = useMemo(
    () =>
      places.filter((p) => {
        if (!placeMatchesLocality(p, locality)) return false;
        if (category !== 'all' && p.category !== category) return false;
        return true;
      }),
    [places, locality, category],
  );

  useEffect(() => {
    if (!mapRef.current || mapObj.current) return;
    const map = L.map(mapRef.current, { zoomControl: true }).setView([HOME.lat, HOME.lng], 13);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
    }).addTo(map);
    layer.current = L.layerGroup().addTo(map);
    mapObj.current = map;
    return () => {
      map.remove();
      mapObj.current = null;
    };
  }, []);

  useEffect(() => {
    if (!mapObj.current || !layer.current) return;
    layer.current.clearLayers();
    L.marker([HOME.lat, HOME.lng], { icon: goldIcon }).addTo(layer.current).bindTooltip(HOME.shortName);
    const bounds = L.latLngBounds([[HOME.lat, HOME.lng]]);
    for (const p of visible) {
      const marker = L.marker([p.lat, p.lng], { icon: navyIcon }).addTo(layer.current);
      marker.bindTooltip(appendEnglish(p.name, p.name));
      marker.on('click', () => setSelected(p));
      bounds.extend([p.lat, p.lng]);
    }
    if (visible.length > 1) {
      mapObj.current.fitBounds(bounds.pad(0.18), { maxZoom: 14, animate: false });
    }
  }, [visible]);

  const setLocality = (next: LocalityFilter) => {
    const q = new URLSearchParams(params);
    if (next === 'both') q.delete('locality');
    else q.set('locality', next);
    setParams(q, { replace: true });
    setSelected(null);
  };

  const setCategory = (next: PlaceFilterCategory) => {
    const q = new URLSearchParams(params);
    if (next === 'all') q.delete('category');
    else q.set('category', next);
    setParams(q, { replace: true });
    setSelected(null);
  };

  return (
    <div className="map-page">
      <div className="page-header">
        <h1 className="page-title">Map</h1>
        <p className="page-subtitle">Gold pin is Rocha Brava · {visible.length} places</p>
      </div>
      <LocalityFilterRow value={locality} onChange={setLocality} />
      <CategoryChipRow value={category} onChange={setCategory} />
      <div className="map-frame" ref={mapRef} role="application" aria-label="Carvoeiro map" />
      {selected ? (
        <div className="map-selected">
          <PlaceCard place={selected} isFavorite={isFavorite(selected.id)} onToggleFavorite={toggle} />
        </div>
      ) : (
        <p className="muted" style={{ marginTop: 12 }}>
          Tap a pin for Maps, Waze, and Call.
        </p>
      )}
    </div>
  );
}
